import React, { createContext, useContext, useState, useEffect } from 'react';
import { localDB, remoteDB, getDbStatus } from '../../db/pouchdb-config';
import pushNotificationManager from '../../services/push-notification-service';

// Create context
const PouchDBContext = createContext(null);

/**
 * PouchDB Provider Component
 * 
 * This component provides PouchDB context to the entire application,
 * handling database initialization, sync, and status monitoring.
 */
export const PouchDBProvider = ({ children }) => {
  const [isInitialized, setIsInitialized] = useState(false);
  const [syncStatus, setSyncStatus] = useState('idle');
  const [lastSynced, setLastSynced] = useState(null);
  const [pendingChanges, setPendingChanges] = useState(0);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [syncHandler, setSyncHandler] = useState(null);
  
  // Initialize PouchDB
  useEffect(() => {
    const initializePouchDB = async () => {
      try {
        setIsInitialized(true);
        
        // Setup notification listener for database changes
        if (localDB && pushNotificationManager.isSupported()) {
          const customerId = sessionStorage.getItem('customerId');
          if (customerId) {
            pushNotificationManager.setupDatabaseListener(localDB);
            console.log('Notification listener setup for database changes');
          }
        }
      } catch (error) {
        console.error('Error initializing PouchDB:', error);
      }
    };
    
    initializePouchDB();
    
    // Cleanup on unmount
    return () => {
      if (syncHandler) {
        syncHandler.cancel();
      }
    };
  }, []);
  
  // Monitor online status
  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
    };
    
    const handleOffline = () => {
      setIsOnline(false);
    };
    
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);
  
  // Start sync with remote database
  const startSync = () => {
    console.log('Sync disabled to prevent resource exhaustion');
    setSyncStatus('disabled');
    return;
  };
  
  // Sync databases manually
  const syncDatabases = async () => {
    if (!localDB || !remoteDB) {
      console.log('Using API client - sync not needed');
      return;
    }
    
    if (!isOnline) {
      console.log('Cannot sync while offline');
      return;
    }
    
    setSyncStatus('syncing');
    try {
      // Push local changes to remote
      await localDB.replicate.to(remoteDB);
      
      // Pull remote changes to local
      await localDB.replicate.from(remoteDB);
      
      setLastSynced(new Date());
      setSyncStatus('completed');
    } catch (error) {
      console.error('Error syncing databases:', error);
      setSyncStatus('error');
    }
  };
  
  // Toggle sync
  const toggleSync = () => {
    if (syncHandler) {
      syncHandler.cancel();
      setSyncHandler(null);
      setSyncStatus('disabled');
    } else {
      startSync();
    }
  };
  
  // Increase max listeners to prevent warning
  useEffect(() => {
    if (localDB && localDB.setMaxListeners) {
      localDB.setMaxListeners(20);
    }
  }, []);
  
  // Context value
  const contextValue = {
    databases: { main: localDB },
    remoteDBs: { main: remoteDB },
    isInitialized,
    syncStatus,
    lastSynced,
    pendingChanges,
    isOnline,
    isSyncing: syncStatus === 'syncing',
    hasRemoteDB: !!remoteDB,
    syncEnabled: !!syncHandler,
    syncDatabases,
    toggleSync
  };
  
  return (
    <PouchDBContext.Provider value={contextValue}>
      {children}
    </PouchDBContext.Provider>
  );
};

// Custom hook to use PouchDB context
export const usePouchDBContext = () => {
  const context = useContext(PouchDBContext);
  if (!context) {
    throw new Error('usePouchDBContext must be used within a PouchDBProvider');
  }
  return context;
};

export default PouchDBProvider;